/**
 * Entering orders that happened before the shop had the software.
 *
 * A shop that traded for a few days before go-live has real sales with no
 * record here. This is where an admin keys them back in, dated to the day they
 * were taken — so the sale lands on that day's books and that day's Orders list,
 * not today's.
 *
 * WHY IT IS ITS OWN SCREEN, NOT THE TILL
 * --------------------------------------
 * Back-dating a sale is a deliberate, admin-only act. Kept on the live till, a
 * date field would be one wrong tap away from filing today's real sale under
 * last week. Here it is out of the way, and only someone who may post to the
 * books can reach it.
 *
 * It records the SALE only — revenue and the cash it came in as. It does not
 * touch stock or the kitchen; the food was made and served days ago.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { formatPKR, today } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Field } from '../../components/ui/Field.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

const TYPES = [
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'dine_in', label: 'Dine in' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'foodpanda', label: 'Foodpanda' },
];
const METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online' },
];

export default function PastOrdersPage() {
  const [orderDate, setOrderDate] = useState(() => today());
  const [orderType, setOrderType] = useState('takeaway');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [search, setSearch] = useState('');
  // productId -> quantity
  const [lines, setLines] = useState({});
  const [done, setDone] = useState(null);
  // Who and where, for a delivery; the Foodpanda app's number, for Foodpanda —
  // noted with the order the same as at the till.
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryCharge, setDeliveryCharge] = useState('');
  const [foodpandaNo, setFoodpandaNo] = useState('');

  const menu = useQuery({
    queryKey: ['menu', 'products'],
    queryFn: () => api.get('/menu/products'),
    staleTime: 5 * 60_000,
  });

  const products = menu.data?.products ?? [];
  const byId = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? products.filter((p) => p.name.toLowerCase().includes(q)) : products;
  }, [products, search]);

  const picked = Object.entries(lines).filter(([, qty]) => qty > 0);
  const estimate =
    picked.reduce((sum, [id, qty]) => sum + (Number(byId[id]?.price) || 0) * qty, 0) +
    (orderType === 'delivery' ? Number(deliveryCharge) || 0 : 0);

  const setQty = (id, qty) =>
    setLines((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });
  const add = (id) => setQty(id, (lines[id] ?? 0) + 1);

  const record = useMutation({
    mutationFn: () =>
      api.post('/pos/orders/backdated', {
        orderType,
        paymentMethod,
        orderDate,
        items: picked.map(([id, qty]) => ({ productId: id, quantity: qty })),
        ...(orderType === 'delivery'
          ? {
              customerName: customerName.trim() || undefined,
              customerPhone: customerPhone.trim() || undefined,
              deliveryAddress: deliveryAddress.trim() || undefined,
              deliveryFee: Number(deliveryCharge) || 0,
            }
          : {}),
        ...(orderType === 'foodpanda' && foodpandaNo.trim()
          ? { externalOrderId: foodpandaNo.trim() }
          : {}),
      }),
    onSuccess: (res) => {
      setDone({ number: res.orderNumber, date: res.orderDate, total: res.totalAmount });
      setLines({});
      setCustomerName('');
      setCustomerPhone('');
      setDeliveryAddress('');
      setDeliveryCharge('');
      setFoodpandaNo('');
      // The date is kept, so several orders for the same day go in one after another.
    },
  });

  const valid = picked.length > 0 && orderDate && orderDate <= today();

  if (menu.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner label="Loading the menu" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-4">
      <header className="mb-4">
        <h1 className="font-semibold text-xl text-ink">Past orders</h1>
        <p className="mt-0.5 text-sm text-ink-2">
          Enter an order the shop took before it had the software. It is dated to the day you choose —
          the sale lands on that day everywhere. Stock and the kitchen are left alone.
        </p>
      </header>

      <div className="grid gap-4 md:grid-cols-[1fr_360px]">
        {/* The menu, to build the order from. */}
        <div className="rounded-2xl border border-rule bg-surface p-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search the menu"
            className="mb-3 h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          />
          <div className="grid max-h-[60vh] grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => add(p.id)}
                className="flex flex-col items-start justify-between gap-1 rounded-lg border border-rule bg-surface-2 p-2.5 text-left transition-colors hover:border-brand-400"
              >
                <span className="text-[13px] leading-tight text-ink">{p.name}</span>
                <span className="flex w-full items-center justify-between">
                  <span className="text-xs font-semibold tabular-nums text-brand-700">
                    {formatPKR(p.price)}
                  </span>
                  {lines[p.id] > 0 && (
                    <span className="rounded-md bg-brand-400 px-1.5 text-xs font-semibold text-surface-2">
                      ×{lines[p.id]}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* The order being recorded. */}
        <div className="rounded-2xl border border-rule bg-surface p-4">
          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Day it was taken"
              type="date"
              value={orderDate}
              max={today()}
              onChange={(e) => e.target.value && setOrderDate(e.target.value)}
            />
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-ink-2">Type</span>
              <select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value)}
                className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              >
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Foodpanda is owed by Foodpanda and settled on its own tab, so there
              is no tender to pick for it. */}
          {orderType === 'foodpanda' ? (
            <p className="mt-3 rounded-lg bg-surface-3 px-3 py-2.5 text-xs text-ink-3">
              Owed by Foodpanda — it goes on the Foodpanda tab and is cleared there when the payout
              lands.
            </p>
          ) : (
            <label className="mt-3 block">
              <span className="mb-1.5 block text-sm font-medium text-ink-2">Paid by</span>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* A delivery is noted with who it went to and where — the same as at
              the till. Optional here, since old orders may be short on detail. */}
          {orderType === 'delivery' && (
            <div className="mt-3 space-y-2">
              <input
                aria-label="Customer name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Name"
                autoComplete="off"
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              <input
                aria-label="Customer phone"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="Phone number"
                inputMode="tel"
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              <input
                aria-label="Delivery address"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="Address"
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
              <input
                aria-label="Delivery charge"
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={deliveryCharge}
                onChange={(e) => setDeliveryCharge(e.target.value)}
                placeholder="Delivery charge (leave blank if none)"
                className="h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm tabular-nums text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
              />
            </div>
          )}

          {/* A Foodpanda order carries the app's own order number. */}
          {orderType === 'foodpanda' && (
            <input
              aria-label="Foodpanda order number"
              value={foodpandaNo}
              onChange={(e) => setFoodpandaNo(e.target.value)}
              placeholder="Foodpanda order #"
              autoComplete="off"
              className="mt-3 h-11 w-full rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            />
          )}

          <div className="mt-4 border-t border-rule pt-3">
            {picked.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-3">
                Tap dishes on the left to build the order.
              </p>
            ) : (
              <ul className="space-y-2">
                {picked.map(([id, qty]) => (
                  <li key={id} className="flex items-center gap-2">
                    <span className="flex-1 truncate text-sm text-ink">{byId[id]?.name}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        aria-label={`Fewer ${byId[id]?.name}`}
                        onClick={() => setQty(id, qty - 1)}
                        className="h-7 w-7 rounded-md border border-rule text-ink-2 hover:bg-rule"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm tabular-nums text-ink">{qty}</span>
                      <button
                        type="button"
                        aria-label={`More ${byId[id]?.name}`}
                        onClick={() => setQty(id, qty + 1)}
                        className="h-7 w-7 rounded-md border border-rule text-ink-2 hover:bg-rule"
                      >
                        +
                      </button>
                    </div>
                    <span className="w-16 text-right text-sm tabular-nums text-ink">
                      {formatPKR((Number(byId[id]?.price) || 0) * qty)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-rule pt-3">
            <span className="text-sm text-ink-2">Total (before tax)</span>
            <span className="text-lg font-semibold tabular-nums text-ink">{formatPKR(estimate)}</span>
          </div>

          {record.isError && (
            <p role="alert" className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
              {record.error?.message ?? 'That did not work.'}
            </p>
          )}

          {done && (
            <p className="mt-3 rounded-lg bg-brand-400/10 px-3.5 py-2.5 text-sm text-ink">
              Recorded {done.number} on {done.date} — {formatPKR(done.total)}. Enter the next one, or
              change the day.
            </p>
          )}

          <Button
            className="mt-4 w-full"
            size="lg"
            busy={record.isPending}
            disabled={!valid || record.isPending}
            onClick={() => {
              setDone(null);
              record.mutate();
            }}
          >
            Record this order
          </Button>
          <p className="mt-2 text-center text-xs text-ink-3">
            The exact total, with tax, is worked out when it is recorded.
          </p>
        </div>
      </div>
    </div>
  );
}
